import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, ChevronRight, Loader2, Receipt, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { donationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import RichTextRenderer from '../components/RichTextRenderer';
import DonationFormDialog from '../components/donations/DonationFormDialog';

const fmtAmount = (cents, currency = 'EUR') => {
  const value = (Number(cents || 0) / 100);
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

const STATUS_LABEL = {
  pending:   { tone: 'text-amber-400',   label: 'En attente' },
  completed: { tone: 'text-emerald-400', label: 'Réussi' },
  failed:    { tone: 'text-rose-400',    label: 'Échec' },
  refunded:  { tone: 'text-text-muted',  label: 'Remboursé' },
  cancelled: { tone: 'text-text-muted',  label: 'Annulé' },
};

const QuestCard = ({ quest, onDonate }) => {
  const goal = quest.goal_amount_cents || 0;
  const raised = quest.raised_amount_cents || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  return (
    <GlassCard className="flex flex-col" data-testid={`quest-card-${quest.slug}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-text-primary leading-tight">{quest.title_fr}</h3>
          {quest.title_ar && (
            <div className="font-arabic text-base text-primary mt-1" dir="rtl">{quest.title_ar}</div>
          )}
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-elevated text-text-muted border border-border">
          {quest.category}
        </span>
      </div>
      {quest.description_html && (
        <div className="text-sm text-text-secondary line-clamp-3 mb-3 [&_*]:text-text-secondary [&_h1]:text-base [&_h2]:text-base">
          <RichTextRenderer value={quest.description_html} testid={`quest-${quest.slug}-summary`} />
        </div>
      )}
      {goal > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-text-muted mb-1">
            <span>{fmtAmount(raised)} collectés</span>
            <span>{fmtAmount(goal)}</span>
          </div>
          <div className="h-1.5 rounded bg-surface-elevated overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      <div className="flex gap-2 mt-auto">
        <Link to={`/dons/quete/${quest.slug}`} className="flex-1">
          <Button variant="outline" className="w-full" data-testid={`quest-${quest.slug}-detail-btn`}>
            <ChevronRight className="w-4 h-4 mr-1" />Détails
          </Button>
        </Link>
        <Button className="flex-1 bg-primary text-background"
                onClick={() => onDonate(quest)}
                data-testid={`quest-${quest.slug}-donate-btn`}>
          <Heart className="w-4 h-4 mr-1" />Faire un don
        </Button>
      </div>
    </GlassCard>
  );
};

const SupportAppCard = ({ onDonate }) => (
  <GlassCard className="border-primary/40" data-testid="quest-app-support">
    <div className="flex items-start gap-3 mb-3">
      <Sparkles className="w-6 h-6 text-primary shrink-0" />
      <div>
        <h3 className="font-serif text-lg text-text-primary">Soutenir Noor El Bayen</h3>
        <p className="text-sm text-text-secondary">
          Votre don nous aide à financer l'hébergement, les développements futurs et à maintenir cette plateforme gratuite et accessible à tous.
        </p>
      </div>
    </div>
    <Button onClick={() => onDonate(null)} className="bg-primary text-background w-full" data-testid="quest-app-support-donate">
      <Heart className="w-4 h-4 mr-1" />Soutenir l'application
    </Button>
  </GlassCard>
);

const MyDonationsTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    donationsApi.myDonations()
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);
  if (!items) return <PageLoading />;
  if (items.length === 0) {
    return (
      <GlassCard className="text-center py-10 text-text-muted">
        <Receipt className="w-8 h-8 mx-auto mb-2" />
        <p>Aucun don pour le moment.</p>
        <p className="text-xs mt-1">Vos dons apparaîtront ici une fois Stripe activé.</p>
      </GlassCard>
    );
  }
  return (
    <div className="space-y-2" data-testid="my-donations-list">
      {items.map((d) => {
        const meta = STATUS_LABEL[d.status] || STATUS_LABEL.pending;
        return (
          <GlassCard key={d.id} className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <div className="font-serif text-text-primary">{d.quest_title || 'Soutien à l\'application'}</div>
              <div className="text-xs text-text-muted">
                {new Date(d.created_at).toLocaleString('fr-FR')} · {d.frequency}
                {d.anonymous && ' · anonyme'}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-text-primary">{fmtAmount(d.amount_cents, d.currency)}</div>
              <div className={`text-xs ${meta.tone}`}>{meta.label}</div>
            </div>
          </GlassCard>
        );
      })}
    </div>
  );
};

const Donations = () => {
  const { user } = useAuth();
  const [quests, setQuests] = useState(null);
  const [activeQuest, setActiveQuest] = useState(undefined); // undefined = closed, null = "support app", obj = quest

  const fetchQuests = useCallback(async () => {
    try {
      const r = await donationsApi.listActiveQuests();
      setQuests(r.data?.items || []);
    } catch {
      setQuests([]);
    }
  }, []);

  useEffect(() => { fetchQuests(); }, [fetchQuests]);

  return (
    <div className="min-h-screen py-8 px-4" data-testid="donations-page">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          overline="Dons"
          title="Soutenez Noor El Bayen et les œuvres partenaires"
          subtitle="Vos dons financent l'application et soutiennent des causes vérifiées par notre équipe."
        />

        {/* Stripe-disabled banner */}
        <div className="mt-4 mb-6 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm" data-testid="donations-stripe-banner">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <span className="text-amber-900 dark:text-amber-200/90">
            Les paiements en ligne seront bientôt disponibles. Vous pouvez déjà <strong>enregistrer un don</strong> ; nous vous recontacterons dès la mise en service.
          </span>
        </div>

        <Tabs defaultValue="quests" className="space-y-6">
          <TabsList className="bg-surface border border-border">
            <TabsTrigger value="quests" data-testid="dons-tab-quests">Quêtes & soutien</TabsTrigger>
            {user && <TabsTrigger value="mine" data-testid="dons-tab-mine">Mes dons</TabsTrigger>}
          </TabsList>

          <TabsContent value="quests">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SupportAppCard onDonate={() => setActiveQuest(null)} />
              {quests === null ? (
                <div className="flex items-center gap-2 text-text-muted"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
              ) : quests.length === 0 ? (
                <GlassCard className="text-center py-10 text-text-muted col-span-full">
                  Aucune quête publiée pour le moment.
                </GlassCard>
              ) : (
                quests.map((q) => <QuestCard key={q.id} quest={q} onDonate={setActiveQuest} />)
              )}
            </div>
          </TabsContent>

          {user && (
            <TabsContent value="mine">
              <MyDonationsTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <DonationFormDialog
        open={activeQuest !== undefined}
        quest={activeQuest}
        onClose={() => setActiveQuest(undefined)}
      />
    </div>
  );
};

export default Donations;
